import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { FaRegCheckCircle } from 'react-icons/fa';
import { RiAlertLine } from 'react-icons/ri';
import { useSelector } from 'react-redux';

import { CircularProgress } from '@material-ui/core';

import Modal from '~/components/Modal';
import { IState } from '~/store';
import { StatusOption } from '~/store/modules/modal/types';
import colors from '~/styles/colors';

import {
  ModalContainer,
  ModalContentContainer,
  ModalSubmitButton,
  ErrorButtonContainer,
  SuccessMessage,
} from './styles';

export interface SubmitModalOptions {
  status?: StatusOption;
  disableError?: boolean;
}
interface SubmitModalData {
  showSubmitModal: (data: boolean) => void;
  toggleSubmitModal: () => void;
  handler: (data: any) => void;
  status: StatusOption;
  setDisableErrorDialogue: (disable: boolean) => void;
}

const SubmitModalContext = createContext<SubmitModalData>(
  {} as SubmitModalData,
);

const SubmitModalProvider: React.FC = ({ children }) => {
  const [submitModal, setSubmitModal] = useState<boolean>(false);
  const [closeAnimation, setCloseAnimation] = useState<boolean>(false);
  const [action, setAction] = useState<() => void>(() => () => {});

  const [disableErrorDialogue, setDisableErrorDialogue] = useState<boolean>(
    false,
  );

  const status = useSelector((state: IState) => state.modal.submitStatus);

  const showSubmitModal = useCallback((data: boolean) => {
    setSubmitModal(data);
  }, []);

  const toggleSubmitModal = useCallback(() => {
    setSubmitModal(!submitModal);
  }, [submitModal]);

  const handler = useCallback((action: () => void) => {
    setSubmitModal(true);
    setAction(() => () => action());
    action();
  }, []);

  useEffect(() => {
    if (status === StatusOption.SUCCESS) {
      setCloseAnimation(false);
      setTimeout(() => {
        setCloseAnimation(true);
        setTimeout(() => setSubmitModal(false), 100);
      }, 1000);
    } else if (status === StatusOption.FAILURE && disableErrorDialogue) {
      setSubmitModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <SubmitModalContext.Provider
      value={{
        showSubmitModal,
        toggleSubmitModal,
        status,
        handler,
        setDisableErrorDialogue,
      }}
    >
      <ModalContainer>
        <Modal
          disableScrollBar
          enableOutsideClick={status === StatusOption.SUCCESS}
          disableCloseButton={status !== StatusOption.FAILURE}
          openSlideAnimation={status === StatusOption.SUCCESS}
          closeSlideAnimation={!closeAnimation}
          open={submitModal}
          modalState={(opened) => setSubmitModal(opened)}
        >
          <div>
            {status === StatusOption.REQUEST ? (
              <CircularProgress
                size={40}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '96px 205px',
                }}
              />
            ) : status === StatusOption.FAILURE ? (
              <ModalContentContainer>
                <RiAlertLine size={56} color={colors.primary} />
                <h2>Ops!</h2>
                <p>Ocorreu um erro ao gravar os seus dados.</p>
                <ErrorButtonContainer>
                  <ModalSubmitButton type="button" onClick={() => action()}>
                    Tentar novamente
                  </ModalSubmitButton>
                </ErrorButtonContainer>
              </ModalContentContainer>
            ) : (
              <ModalContentContainer>
                <FaRegCheckCircle size={40} color={colors.darkgreen} />
                <SuccessMessage>
                  Solicitação enviada com sucesso!
                </SuccessMessage>
              </ModalContentContainer>
            )}
          </div>
        </Modal>
      </ModalContainer>
      {children}
    </SubmitModalContext.Provider>
  );
};

function useSubmitModal(options?: SubmitModalOptions) {
  const context = useContext(SubmitModalContext);

  if (!context) {
    throw new Error(
      'useSubmitModal must be used within an SubmitModalProvider',
    );
  }

  if (options?.disableError) {
    context.setDisableErrorDialogue(options.disableError);
  }

  return context;
}

export { SubmitModalProvider, useSubmitModal, StatusOption };
